import { AnimatePresence, motion } from 'motion/react';
import { Trans } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import ShellGreeting from '@/components/app-shell/ShellGreeting';
import LessonNeighborsNavContainer from '@/components/lesson/LessonNeighborsNavContainer';
import { PageHeader } from '@/components/shared/PageHeader';
import ActiveCourseSelect from '@/components/student-dashboard/ActiveCourseSelect';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { useAppShell } from '@/data/context/AppShellContext';
import useShellGoBack from '@/data/hook/app-shell/useShellGoBack';
import {
  ShellDesktopHeaderVariant,
  ShellLessonParams,
} from '@/data/hook/app-shell/useShellDesktopHeader';
import {
  accordionMarginRight,
  accordionMarginTop,
  expoOut,
} from '@/utils/animations/commom';

type ShellDesktopHeaderProps = {
  variant: ShellDesktopHeaderVariant;
  lessonParams?: ShellLessonParams;
};

export default function ShellDesktopHeader({
  variant,
  lessonParams,
}: ShellDesktopHeaderProps) {
  const goBack = useShellGoBack();
  const { breadcrumbsTarget } = useAppShell();
  const isDashboard = variant === 'dashboard';

  return (
    <PageHeader className="min-w-0 gap-0 lg:py-4">
      <PageHeader.Row className="flex-nowrap justify-between">
        <div className="flex min-w-0 items-center">
          <AnimatePresence initial={false}>
            {!isDashboard && (
              <motion.div
                key="back"
                {...accordionMarginRight}
                className="hidden overflow-hidden lg:block"
              >
                <PageHeader.Back onClick={goBack} />
              </motion.div>
            )}
          </AnimatePresence>
          <PageHeader.Title
            className={twMerge(
              'truncate',
              isDashboard && 'hidden text-4xl leading-12 lg:block',
            )}
          >
            <ConditionalRenderer
              condition={isDashboard}
              fallback={
                <Trans
                  i18nKey="courseLessons.header.title"
                  components={{
                    highlight: <span className="text-primary" />,
                  }}
                />
              }
            >
              <ShellGreeting />
            </ConditionalRenderer>
          </PageHeader.Title>
        </div>
        <PageHeader.Actions>
          <ActiveCourseSelect />
        </PageHeader.Actions>
      </PageHeader.Row>
      <AnimatePresence initial={false}>
        {!isDashboard && (
          <motion.div
            key="breadcrumbs"
            {...accordionMarginTop}
            transition={expoOut}
            className="overflow-hidden"
          >
            <PageHeader.Row className="flex-nowrap justify-between">
              <ConditionalRenderer condition={breadcrumbsTarget}>
                <PageHeader.Breadcrumbs
                  items={breadcrumbsTarget?.items ?? []}
                  className="min-w-0 flex-1 overflow-hidden"
                  itemClassName="min-w-0"
                />
              </ConditionalRenderer>
              {lessonParams && (
                <LessonNeighborsNavContainer
                  {...lessonParams}
                  className="shrink-0"
                />
              )}
            </PageHeader.Row>
          </motion.div>
        )}
      </AnimatePresence>
    </PageHeader>
  );
}
